import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { PageHeader, Tile, Loading } from "@/components/Bits";
import { FloppyDisk, Key, Eye, EyeSlash, CurrencyEur, EyeClosed } from "@phosphor-icons/react";
import { useShowPrices } from "@/showPrices";

export default function Settings() {
  const { t } = useI18n();
  const { refreshFx } = useCurrency();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const update = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    const body = {
      current_year: parseInt(s.current_year),
      method_switch: parseInt(s.method_switch),
      prob_wabtec: parseFloat(s.prob_wabtec),
      prob_tbc: parseFloat(s.prob_tbc),
      prob_competitor: parseFloat(s.prob_competitor),
      timing_now: parseFloat(s.timing_now),
      timing_short: parseFloat(s.timing_short),
      timing_mid: parseFloat(s.timing_mid),
      timing_long: parseFloat(s.timing_long),
      timing_mode: s.timing_mode || "step",
      timing_tau: parseFloat(s.timing_tau) || 3.0,
      fx_eur_try: parseFloat(s.fx_eur_try),
      fx_eur_usd: parseFloat(s.fx_eur_usd),
    };
    await api.put("/settings", body);
    await refreshFx();
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (!s) return <Loading />;

  const Row = ({ label, k, step = "0.01", min = 0, max }) => (
    <div className="grid grid-cols-[1fr_120px] gap-3 items-center py-2 border-b border-white/5">
      <div className="text-sm text-zinc-300">{label}</div>
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        value={s[k] ?? 0}
        onChange={(e) => update(k, e.target.value)}
        className="input-tech num"
        data-testid={`set-${k}`}
      />
    </div>
  );

  return (
    <div data-testid="settings-page">
      <PageHeader
        title={t("settings.title")}
        subtitle={t("settings.subtitle")}
        right={
          <button onClick={save} disabled={saving} className="btn-accent flex items-center gap-2" data-testid="settings-save">
            <FloppyDisk size={14} />
            {saving ? t("common.loading") : t("settings.save")}
          </button>
        }
      />
      <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <Tile testId="settings-method">
          <div className="label-tech mb-3">{t("settings.method")}</div>
          <div className="flex gap-2 mb-2">
            {[1, 2, 3].map((m) => (
              <button
                key={m}
                onClick={() => update("method_switch", m)}
                className={`px-4 py-2 text-xs font-mono ${s.method_switch === m ? "bg-wabtec-red text-white" : "border border-white/15 text-zinc-300 hover:bg-white/5"}`}
                data-testid={`set-method-${m}`}
              >
                M{m} — {["Theoretical", "Addressable", "Weighted"][m - 1]}
              </button>
            ))}
          </div>
          <Row label={t("settings.year")} k="current_year" step="1" min={2020} max={2040} />
        </Tile>

        <Tile testId="settings-prob">
          <div className="label-tech mb-3">{t("settings.probabilities")}</div>
          <Row label={t("whatif.probWabtec")} k="prob_wabtec" max={1} />
          <Row label={t("whatif.probTbc")} k="prob_tbc" max={1} />
          <Row label={t("whatif.probComp")} k="prob_competitor" max={1} />
        </Tile>

        <Tile testId="settings-timing">
          <div className="label-tech mb-3">{t("settings.timing")}</div>

          {/* Mode toggle */}
          <div className="mb-4">
            <div className="text-[12px] mb-2" style={{ color: "var(--text-secondary)" }}>Mode</div>
            <div className="flex gap-1.5">
              {[
                { v: "step", label: "Step (4 buckets)", desc: "Hot/Warm/Cold step thresholds" },
                { v: "exp",  label: "Exponential e^(-y/τ)", desc: "Smooth decay, no cliffs" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => update("timing_mode", opt.v)}
                  data-testid={`set-tmode-${opt.v}`}
                  title={opt.desc}
                  className="px-3 py-1.5 text-xs font-mono rounded-full transition-colors"
                  style={
                    (s.timing_mode || "step") === opt.v
                      ? { background: "var(--text-primary)", color: "#fff", border: "none" }
                      : { background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border)" }
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {(s.timing_mode || "step") === "exp" ? (
            <>
              <Row label="τ (years) — half-life ≈ τ × ln(2)" k="timing_tau" step="0.5" min={0.5} max={20} />
              <div className="text-[11px] mt-2 font-mono" style={{ color: "var(--text-tertiary)" }}>
                With τ={Number(s.timing_tau || 3).toFixed(1)}:&nbsp;
                1y → {Math.exp(-1 / (Number(s.timing_tau) || 3)).toFixed(2)} ·&nbsp;
                3y → {Math.exp(-3 / (Number(s.timing_tau) || 3)).toFixed(2)} ·&nbsp;
                5y → {Math.exp(-5 / (Number(s.timing_tau) || 3)).toFixed(2)} ·&nbsp;
                10y → {Math.exp(-10 / (Number(s.timing_tau) || 3)).toFixed(2)}
              </div>
            </>
          ) : (
            <>
              <Row label={t("whatif.timingNow")} k="timing_now" max={1} />
              <Row label={t("whatif.timingShort")} k="timing_short" max={1} />
              <Row label={t("whatif.timingMid")} k="timing_mid" max={1} />
              <Row label={t("whatif.timingLong")} k="timing_long" max={1} />
            </>
          )}
        </Tile>

        <Tile testId="settings-fx">
          <div className="label-tech mb-3">{t("settings.fx")}</div>
          <Row label="EUR → TRY" k="fx_eur_try" step="0.1" max={100} />
          <Row label="EUR → USD" k="fx_eur_usd" step="0.01" max={5} />
        </Tile>

        <div className="md:col-span-2">
          <NotificationPrefsTile />
        </div>

        <div className="md:col-span-2">
          <ChangePasswordTile />
        </div>

        <div className="md:col-span-2">
          <ShowPricesTile />
        </div>

        {saved && (
          <div className="md:col-span-2 tile p-3 border-wabtec-red text-wabtec-red text-sm font-mono">
            ✓ Saved. Opportunities recomputed.
          </div>
        )}
      </div>
    </div>
  );
}


function NotificationPrefsTile() {
  const { t, lang } = useI18n();
  const [p, setP] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/notifications/preferences").then((r) => setP(r.data));
  }, []);

  const flip = async (key, value) => {
    const next = { ...p, [key]: value };
    setP(next);
    await api.put("/notifications/preferences", { [key]: value });
    setSavedAt(Date.now());
  };

  const triggerScan = async () => {
    await api.post("/notifications/scan-now");
    setSavedAt(Date.now());
  };

  if (!p) return null;

  const COPY = lang === "fr" ? {
    title: "Notifications",
    subtitle: "Toutes les alertes restent dans l'app (zéro email, zéro webhook).",
    ovh: { name: "🔥 Opportunités urgentes (OVH Past/Now)", desc: "Alerte hebdo avec les leads en zone OVH urgente." },
    stale: { name: "📋 Plans inactifs", desc: "Alerte si un plan de compte n'a pas été touché depuis N jours (0 = désactivé)." },
    shift: { name: "📈 Variation de pipeline", desc: "Notifie quand le pipeline M3 bouge de ±5 % d'une semaine sur l'autre." },
    risk: { name: "⚠️ Concentration de risque", desc: "Alerte si plus de 50 % du pipeline dépend d'un seul opérateur." },
    ai: { name: "🧠 Résumé IA hebdo", desc: "Chaque lundi matin, l'IA résume votre semaine en 3-5 points." },
    comments: { name: "💬 Commentaires", desc: "Notifie quand quelqu'un commente une opportunité ou un plan que vous suivez." },
    scanNow: "Tester maintenant",
  } : {
    title: "Notifications",
    subtitle: "All alerts stay in-app (no email, no webhook).",
    ovh: { name: "🔥 Hot opportunities (OVH Past/Now)", desc: "Weekly alert with the urgent OVH leads." },
    stale: { name: "📋 Stale plans", desc: "Alert when an account plan has not been touched in N days (0 = off)." },
    shift: { name: "📈 Pipeline shift", desc: "Notifies when the M3 pipeline moves ±5 % week-over-week." },
    risk: { name: "⚠️ Risk concentration", desc: "Alert when over 50 % of the pipeline depends on a single operator." },
    ai: { name: "🧠 AI weekly summary", desc: "Every Monday morning, the AI summarises your week in 3-5 bullet points." },
    comments: { name: "💬 Comments", desc: "Notifies when someone comments on an opportunity or plan you follow." },
    scanNow: "Test now",
  };

  return (
    <div className="tile p-4 md:p-7" data-testid="settings-notifs">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="label-tech">{COPY.title}</div>
          <p className="text-[13px] mt-1.5" style={{ color: "var(--text-secondary)" }}>{COPY.subtitle}</p>
        </div>
        <button onClick={triggerScan} className="btn-ghost text-[12px] shrink-0" data-testid="notifs-scan-now">{COPY.scanNow}</button>
      </div>

      <div className="space-y-2">
        <PrefRow label={COPY.ovh.name} desc={COPY.ovh.desc} on={p.rule_ovh_past_now} onChange={(v) => flip("rule_ovh_past_now", v)} testKey="ovh" />
        <PrefRow label={COPY.risk.name} desc={COPY.risk.desc} on={p.rule_risk_concentration} onChange={(v) => flip("rule_risk_concentration", v)} testKey="risk" />
        <PrefRow label={COPY.shift.name} desc={COPY.shift.desc} on={p.rule_pipeline_shift} onChange={(v) => flip("rule_pipeline_shift", v)} testKey="shift" />
        <PrefRow label={COPY.ai.name} desc={COPY.ai.desc} on={p.ai_weekly_summary} onChange={(v) => flip("ai_weekly_summary", v)} testKey="ai" />
        <PrefRow label={COPY.comments.name} desc={COPY.comments.desc} on={p.comment_notifications} onChange={(v) => flip("comment_notifications", v)} testKey="comments" />

        <div className="p-3 rounded-[10px]" style={{ background: "var(--bg-subtle)" }} data-testid="pref-row-stale">
          <div className="text-[13px] font-medium">{COPY.stale.name}</div>
          <div className="text-[11.5px] mb-2" style={{ color: "var(--text-secondary)" }}>{COPY.stale.desc}</div>
          <input
            type="number"
            min={0} max={365}
            value={p.rule_stale_plan_days}
            onChange={(e) => flip("rule_stale_plan_days", parseInt(e.target.value || "0"))}
            className="input-tech max-w-[120px]"
            data-testid="notifs-stale-days"
          />
        </div>
      </div>

      {savedAt && (
        <div className="text-[11.5px] font-mono mt-3" style={{ color: "var(--success)" }}>✓ Saved</div>
      )}
    </div>
  );
}


function PrefRow({ label, desc, on, onChange, testKey }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-[10px] hover:bg-[var(--bg-subtle)] cursor-pointer transition-colors" data-testid={`pref-row-${testKey}`}>
      <input
        type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)}
        className="accent-[var(--accent)] w-4 h-4 mt-0.5"
        data-testid={`pref-toggle-${testKey}`}
      />
      <div className="flex-1">
        <div className="text-[13px] font-medium">{label}</div>
        <div className="text-[11.5px]" style={{ color: "var(--text-secondary)" }}>{desc}</div>
      </div>
    </label>
  );
}


function ChangePasswordTile() {
  const { lang } = useI18n();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const L = (fr, en) => (lang === "fr" ? fr : en);

  const submit = async (e) => {
    e.preventDefault();
    if (next1 !== next2) {
      toast.error(L("Les deux mots de passe ne correspondent pas", "Passwords do not match"));
      return;
    }
    if (next1.length < 6) {
      toast.error(L("Mot de passe trop court (6 caractères min)", "Password too short (6 chars min)"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next1,
      });
      toast.success(L("Mot de passe modifié ✓", "Password changed ✓"));
      setCurrent(""); setNext1(""); setNext2("");
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec du changement", "Change failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="tile p-4 md:p-7" data-testid="settings-password">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Key size={14} weight="fill" style={{ color: "var(--accent)" }} />
          <div>
            <div className="label-tech">{L("Changer mon mot de passe", "Change my password")}</div>
            <p className="text-[12px] mt-1" style={{ color: "var(--text-secondary)" }}>
              {L("Modifiez votre propre mot de passe en toute autonomie. Minimum 6 caractères.",
                 "Update your own password securely. Minimum 6 characters.")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="btn-ghost text-[12px] flex items-center gap-1.5 shrink-0"
          data-testid="password-toggle-show"
        >
          {show ? <EyeSlash size={13} /> : <Eye size={13} />}
          {show ? L("Masquer", "Hide") : L("Afficher", "Show")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <PwdField label={L("Mot de passe actuel", "Current password")}     value={current} set={setCurrent} testId="password-current" autoComplete="current-password" show={show} />
        <PwdField label={L("Nouveau mot de passe", "New password")}        value={next1}   set={setNext1}   testId="password-new"     autoComplete="new-password"     show={show} />
        <PwdField label={L("Confirmer le nouveau", "Confirm new")}         value={next2}   set={setNext2}   testId="password-confirm" autoComplete="new-password"     show={show} />
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="submit"
          disabled={busy || !current || !next1 || !next2}
          className="btn-accent flex items-center gap-2"
          data-testid="password-submit"
        >
          <Key size={13} weight="bold" />
          {busy ? L("Enregistrement…", "Saving…") : L("Mettre à jour", "Update")}
        </button>
      </div>
    </form>
  );
}


function PwdField({ label, value, set, testId, autoComplete, show }) {
  return (
    <label className="block">
      <div className="text-[11px] mb-1.5 font-mono uppercase tracking-wide" style={{ color: "var(--text-tertiary)" }}>{label}</div>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => set(e.target.value)}
        autoComplete={autoComplete}
        data-testid={testId}
        className="w-full px-3 py-2 text-[14px] font-mono rounded-[10px]"
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", color: "var(--text-primary)" }}
      />
    </label>
  );
}


function ShowPricesTile() {
  const { lang } = useI18n();
  const { showPrices, setShowPrices } = useShowPrices();
  const L = (fr, en) => (lang === "fr" ? fr : en);

  return (
    <div className="tile p-4 md:p-7" data-testid="settings-show-prices">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center"
             style={{ background: showPrices ? "rgba(13,113,221,0.10)" : "var(--bg-subtle)" }}>
          {showPrices
            ? <CurrencyEur size={16} weight="fill" style={{ color: "#0D71DD" }} />
            : <EyeClosed size={16} weight="fill" style={{ color: "var(--text-tertiary)" }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="label-tech">{L("Affichage des prix", "Price visibility")}</div>
              <p className="text-[12.5px] mt-1" style={{ color: "var(--text-secondary)", maxWidth: 560 }}>
                {showPrices
                  ? L("Les colonnes en € (M1/M2/M3, valeur active, prix unitaire) sont affichées. Les opportunités sans prix saisis afficheront « — ».",
                       "Price columns (M1/M2/M3, active value, unit price) are visible. Opportunities without a price will show '—'.")
                  : L("Aucun prix n'est affiché dans l'application. Les opportunités sont triées par quantité d'équipements et échéance OVH. Idéal si les prix sont confidentiels.",
                       "All prices are hidden across the app. Opportunities are ranked by equipment quantity and OVH due date. Recommended when prices are confidential.")}
              </p>
              <p className="text-[11.5px] mt-1.5 font-medium" style={{ color: "var(--text-tertiary)", maxWidth: 560 }}>
                {L("Ce réglage ne concerne que les administrateurs. Les rôles Sales et Viewer ne voient jamais les prix, quel que soit ce réglage.",
                   "This setting only affects administrators. Sales and Viewer roles never see prices, regardless of this toggle.")}
              </p>
            </div>
            <label className="cursor-pointer shrink-0">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={showPrices}
                  onChange={(e) => setShowPrices(e.target.checked)}
                  data-testid="show-prices-toggle"
                  className="sr-only"
                />
                <span
                  className="relative inline-flex w-11 h-6 rounded-full transition-colors duration-200"
                  style={{ background: showPrices ? "#0D71DD" : "var(--border)" }}
                >
                  <span
                    className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200"
                    style={{ transform: showPrices ? "translateX(20px)" : "translateX(0)" }}
                  />
                </span>
                <span className="text-[12px] font-mono uppercase tracking-wide"
                      style={{ color: showPrices ? "var(--text-primary)" : "var(--text-tertiary)" }}>
                  {showPrices ? L("Activé", "ON") : L("Désactivé", "OFF")}
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
